import { useForm, Controller } from 'react-hook-form';
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, FormFeedback, Label, Form, Input } from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiStar, mdiChevronLeft } from '@mdi/js';
import {
  fetchEmployeeLeaveTypes,
  fetchVacationBalance,
  fetchDelegateEmployerAutoComplete,
  applyLeave,
  fetchDepartmentLeaveStatus,
  fetchLeaveByTransactionId,
} from '../../../store/apis/leaveService';
import { fetchLeaveAndActualDaysAutoComplete } from '../../../store/apis/autocompleteService';
import {
  initialValues,
  validationSchema,
  halfDayLeaveTypes,
  appliedLeaveDataConfig,
  departmentDataConfig,
  populateFormValues,
  validateLeaveSchema,
} from './constants';
import {
  ErrorModalWrapper,
  ErrorTable,
  UserAsyncAutocompleteSelect,
  FileUploadDropzone,
  TimePicker,
  DatePicker,
} from '../../../components/Common';
import TimeOffHistory from '../components/TimeOffHistory';
import { downloadBlobType } from '../../../store/apis/commonService';
import { DateUtil, StringUtil } from '../../../utils';
import { getAuthUser } from '../../../utils/auth.util';
import { getCompanyCode } from '../../../config/axios.config';
import { companySet1, companySet2 } from '../constants';
import '../index.scss';

function ApplyLeave() {
  document.title = 'Apply Leave | Envision - Daikin';
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { CoCd, ID, EmpCode } = Object.fromEntries(query);
  const [errorModal, setErrorModal] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  const fileInputRef = useRef(null);
  const formType = ID ? 'view' : 'apply';
  const selectedCoCd = getCompanyCode();

  const DEPT_LEAVE_TOAST_ID = 'deptLeaveStatusError';

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isValid },
    setError,
    clearErrors,
    trigger,
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validateLeaveSchema),
    mode: 'onChange',
  });
  const watchValues = watch();

  const toggleErrorModal = () => setErrorModal(!errorModal);

  const { data: leaveTypes } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: fetchEmployeeLeaveTypes,
    keepPreviousData: true,
  });

  const getLeaveTypeDescription = code => {
    const leaveType = leaveTypes?.find(lt => lt.Code === code);
    return leaveType ? StringUtil.toTitleCase(leaveType?.Description) : '';
  };

  const { data: vacationBalance } = useQuery({
    queryKey: ['vacationBalance'],
    queryFn: fetchVacationBalance,
    keepPreviousData: true,
  });

  const departmentLeaveMutation = useMutation({
    mutationFn: fetchDepartmentLeaveStatus,
  });

  const { mutate: applyLeaveMutation, isPending: applyLeavePending } =
    useMutation({
      mutationFn: formData => applyLeave(formData),
      onSuccess: (data, params) => {
        if (data.flag === 'False') {
          if (
            data?.message === 'Leave cannot be applied for Prior Calendar Year.'
          ) {
            data.isTable = false;
          } else {
            data.isTable = true;
            setModalConfig(appliedLeaveDataConfig);
          }
          setErrorModal(true);
          setErrorData(data);
        } else {
          toast.success(
            `${getLeaveTypeDescription(params.get('leaveType').trim())} Request #${data?.transactionNo} Has Been Submitted Successfully`,
            {
              className: 'toast-success',
            },
          );
          reset();
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      },
      onError: () => {
        toast.error('Error submitting leave application. Please try again.', {
          className: 'toast-error',
        });
      },
    });

  const { data: leaveData } = useQuery({
    queryKey: ['leaveData', ID],
    queryFn: () =>
      fetchLeaveByTransactionId({
        transNo: ID,
        employeeId: EmpCode || getAuthUser()?.employeeId,
        companyCode: CoCd,
      }),
    enabled: formType === 'view',
  });

  const downloadFileMutation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const { data: leaveAndActualDaysData } = useQuery({
    queryKey: [
      'fetchLeaveAndActualDays',
      watchValues.leaveType,
      watchValues.leaveFromDate,
      watchValues.leaveToDate,
    ],
    queryFn: () =>
      fetchLeaveAndActualDaysAutoComplete({
        leaveType: watchValues.leaveType,
        leaveFromDate: watchValues.leaveFromDate,
        leaveToDate: watchValues.leaveToDate,
      }),
    enabled:
      !!watchValues.leaveType &&
      !!watchValues.leaveFromDate &&
      !!watchValues.leaveToDate,
  });

  const onSubmit = values => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'file' && value && value.length > 0) {
        value.forEach(file => formData.append(key, file));
      } else {
        formData.append(key, value || '');
      }
    });
    formData.set('entitledDays', vacationBalance?.Entitled || 0);
    formData.set('usedDays', vacationBalance?.Used || 0);
    formData.set('balanceDays', vacationBalance?.Balance || 0);
    formData.set('extraDays', vacationBalance?.Extra || 0);
    formData.set('delegateApprover', values?.delegateApprover?.value || 0);

    // Convert date to Correct format
    formData.set(
      'leaveFromDate',
      DateUtil.formatDateObjToHtmlDateTime(values.leaveFromDate),
    );
    formData.set(
      'leaveToDate',
      DateUtil.formatDateObjToHtmlDateTime(values.leaveToDate),
    );
    applyLeaveMutation(formData);
  };

  const handleDepartmentLeaveStatus = async () => {
    let error = false;
    if (!watchValues.leaveType) {
      setError('leaveType', {
        type: 'manual',
        message: 'Leave type is required',
      });
      error = true;
    }
    if (!watchValues.leaveFromDate) {
      setError('leaveFromDate', {
        type: 'manual',
        message: 'Leave from date is required',
      });
      error = true;
    }
    if (!watchValues.leaveToDate) {
      setError('leaveToDate', {
        type: 'manual',
        message: 'Leave to date is required',
      });
      error = true;
    }
    // Check if leaveToDate is greater than leaveFromDate
    if (
      new Date(watchValues.leaveToDate) < new Date(watchValues.leaveFromDate)
    ) {
      setError('leaveToDate', {
        type: 'manual',
        message: 'Leave To Date cannot be earlier than Leave From Date',
      });
      error = true;
    }

    if (error) {
      if (!toast.isActive(DEPT_LEAVE_TOAST_ID)) {
        toast.error(
          'Please fill in all required fields to view department leave status.',
          { className: 'toast-error' },
          { toastId: DEPT_LEAVE_TOAST_ID },
        );
      }
      return;
    }

    const response = await departmentLeaveMutation.mutateAsync({
      leaveType: watchValues.leaveType,
      leaveFromDate: DateUtil.formatDateObjToHtmlDateTime(
        new Date(watchValues.leaveFromDate),
      ),
      leaveToDate: DateUtil.formatDateObjToHtmlDateTime(
        new Date(watchValues.leaveToDate),
      ),
    });
    toggleErrorModal();
    setErrorData({
      message: 'Department Leave Status',
      errorData: response,
      isTable: true,
    });
    setModalConfig(departmentDataConfig);
  };

  useEffect(() => {
    if (formType !== 'view' || !leaveData?.length) return;

    const editFormValues = populateFormValues(leaveData[0]);
    reset(editFormValues);

    const raw = editFormValues.attachmentPath;
    if (!raw || raw === '-' || raw.trim() === '') return;

    let attachmentArray = [];
    attachmentArray = StringUtil.checkStringOrJsonArray(raw);
    (async () => {
      try {
        const files = await Promise.all(
          attachmentArray.map(async fileName => {
            const response = await downloadFileMutation.mutateAsync({
              fileName,
              type: 'Leave Documents',
              hrCoCd: leaveData[0].HRCoCd.trim(),
            });
            return new File([response], fileName, { type: response.type });
          }),
        );
        setValue('file', files);
        if (fileInputRef.current) {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(files);
          fileInputRef.current.files = dataTransfer.files;
        }
      } catch (err) {
        toast.error('Error downloading attachments. Please try again.', {
          className: 'toast-error',
        });
      }
    })();
  }, [formType, leaveData, reset, setValue]);

  useEffect(() => {
    if (errorData && !errorData?.isTable) {
      toast.error(errorData?.message || 'An error occurred', {
        className: 'toast-error',
      });
    }
  }, [errorData]);

  useEffect(() => {
    const { leaveFromDate, leaveToDate } = watchValues;

    if (leaveFromDate && leaveToDate) {
      const from = new Date(leaveFromDate);
      const to = new Date(leaveToDate);

      if (to < from) {
        // Clear first to ensure clean state
        clearErrors(['leaveFromDate', 'leaveToDate']);

        // Set errors with slight delays to ensure both are set
        setTimeout(() => {
          setError('leaveFromDate', {
            type: 'manual',
            message: 'Leave From Date cannot be after Leave To Date',
          });
        }, 0);

        setTimeout(() => {
          setError('leaveToDate', {
            type: 'manual',
            message: 'Leave To Date cannot be earlier than Leave From Date',
          });
        }, 10);
      } else {
        clearErrors(['leaveFromDate', 'leaveToDate']);
      }
    }
  }, [
    watchValues.leaveFromDate,
    watchValues.leaveToDate,
    setError,
    clearErrors,
  ]);

  return (
    <div className="page-content apply-forms px-0">
      <div className="d-flex flex-row align-items-stretch p-0">
        <div className="col-7 p-0 h-100">
          <div className="d-flex flex-row align-items-center justify-content-between leave-layout-border-bottom">
            {formType !== 'view' ? (
              <div className="ps-3 pb-2 pt-3 d-flex align-items-center fs-5 fw-semibold apply-header">
                Apply Leave
              </div>
            ) : (
              <div className="ps-1 py-3 d-flex flex-row align-items-center gap-2 apply-header fs-5">
                <button
                  type="button"
                  className="btn btn-link p-0 back-btn fs-5"
                  onClick={() => window.history.back()}
                >
                  <Icon path={mdiChevronLeft} size={1.1} />
                  <span className="back fs-5">Back</span>
                </button>
                <div
                  style={{
                    width: '1px',
                    borderLeft: '1px solid lightgrey',
                    height: '20px',
                  }}
                />
                <div className="d-flex flex-row gap-1">
                  <span className="request-id-span fs-5">Request ID</span>
                  <span className="request-id-span-value fs-5">{ID}</span>
                </div>
              </div>
            )}
          </div>
          <Form
            className="d-flex flex-column gap-1 p-3"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div>
              <Label for="leaveType" className="my-1">
                Leave Type<span className="text-danger">*</span>
              </Label>
              <Controller
                name="leaveType"
                control={control}
                render={({ field }) => (
                  <Input
                    type="select"
                    {...field}
                    className={`form-select external-filter-input ${errors.leaveType ? 'is-invalid' : ''}`}
                    disabled={formType === 'view'}
                    value={watchValues.leaveType}
                    title="Select the type of leave you want to apply for from the available dropdown options."
                  >
                    <option value="">Select Leave Types</option>
                    {leaveTypes?.map(leaveType => (
                      <option key={leaveType.Code} value={leaveType.Code}>
                        {leaveType.Description}
                      </option>
                    ))}
                  </Input>
                )}
              />
              {errors.leaveType && (
                <FormFeedback>{errors.leaveType.message}</FormFeedback>
              )}
            </div>

            <div className="d-flex flex-row gap-2">
              <div className="w-100">
                <Label className="my-1">
                  From<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="leaveFromDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      disabled={formType === 'view'}
                      className={`external-filter-input ${errors.leaveFromDate ? 'is-invalid' : ''}`}
                      error={!!errors.leaveFromDate}
                      title="Choose the starting date of your leave period."
                    />
                  )}
                />
                {errors.leaveFromDate && (
                  <div className="invalid-feedback d-block">
                    {errors.leaveFromDate.message}
                  </div>
                )}
              </div>
              <div className="w-100">
                <Label className="my-1">
                  To<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="leaveToDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      disabled={formType === 'view'}
                      className={`external-filter-input ${errors.leaveToDate ? 'is-invalid' : ''}`}
                      error={!!errors.leaveToDate}
                      title="Choose the ending date of your leave period."
                    />
                  )}
                />
                {errors.leaveToDate && (
                  <div className="invalid-feedback d-block">
                    {errors.leaveToDate.message}
                  </div>
                )}
              </div>
            </div>
            {leaveAndActualDaysData && (
              <div className="leave-days-info gap-2">
                <div
                  className="d-flex flex-row gap-1"
                  title="Number of leave days calculated directly using the entered start and end dates."
                >
                  <span className="fw-bold">Leave Days: </span>
                  <span className="fw-bold">
                    {parseFloat(leaveAndActualDaysData?.leaveDays).toFixed(1) ||
                      0}
                  </span>
                </div>
                <span className="fw-bold"> | </span>
                <div
                  className="d-flex flex-row gap-1"
                  title="Number of leave days excluding any weekends and public holidays."
                >
                  <span className="fw-bold">Actual Days: </span>
                  <span className="fw-bold">
                    {parseFloat(leaveAndActualDaysData?.actualDays).toFixed(
                      1,
                    ) || 0}
                  </span>
                </div>
              </div>
            )}

            {halfDayLeaveTypes.includes(watchValues.leaveType) && (
              <div className="d-flex flex-row gap-2">
                <div className="w-100">
                  <Label className="my-1">From Time</Label>
                  <Controller
                    name="leaveFromTime"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        {...field}
                        interval={15}
                        disabled={formType === 'view'}
                        className={`mpi-time-picker external-filter-input ${errors.leaveFromTime ? 'is-invalid' : ''}`}
                        error={!!errors.leaveFromTime}
                        title="Choose the starting time of your leave period."
                      />
                    )}
                  />
                  {errors.leaveFromTime && (
                    <div className="invalid-feedback d-block">
                      {errors.leaveFromTime.message}
                    </div>
                  )}
                </div>
                <div className="w-100">
                  <Label className="my-1">To Time</Label>
                  <Controller
                    name="leaveToTime"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        {...field}
                        interval={15}
                        disabled={formType === 'view'}
                        className={`mpi-time-picker external-filter-input ${errors.leaveToTime ? 'is-invalid' : ''}`}
                        error={!!errors.leaveToTime}
                        title="Choose the ending time of your leave period."
                      />
                    )}
                  />
                  {errors.leaveToTime && (
                    <div className="invalid-feedback d-block">
                      {errors.leaveToTime.message}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label className="my-1">
                Reason<span className="text-danger">*</span>
              </Label>
              <Controller
                name="reasonForLeave"
                control={control}
                render={({ field }) => (
                  <Input
                    type="text"
                    placeholder="Enter Reason for Leave"
                    {...field}
                    className={`form-control external-filter-input ${errors.reasonForLeave ? 'is-invalid' : ''}`}
                    disabled={formType === 'view'}
                    title="Briefly mention the reason for requesting this leave."
                  />
                )}
              />
              {errors.reasonForLeave && (
                <FormFeedback>{errors.reasonForLeave.message}</FormFeedback>
              )}
            </div>

            <div>
              <Label className="my-1">Delegate Approver</Label>
              <Controller
                name="delegateApprover"
                control={control}
                render={() => (
                  <UserAsyncAutocompleteSelect
                    autoCompleteFn={input =>
                      fetchDelegateEmployerAutoComplete({
                        type: 'EMPLOYEE',
                        prefixText: input,
                      })
                    }
                    mappedFn={rawData =>
                      rawData.map(item => ({
                        value: item.ID,
                        label: item.Name,
                        name: item.Name,
                        code: item.Cd,
                        designation: item.Designation,
                        image: item?.Imagefile,
                      }))
                    }
                    onChange={e => {
                      setValue('delegateApprover', e || '');
                    }}
                    placeholder="Search by Employee Name & ID"
                    isClearable
                    isSearchable
                    value={watchValues.delegateApprover}
                    isDisabled={formType === 'view'}
                    tooltipTitle="Select an alternate approver if your primary approver is unavailable."
                  />
                )}
              />
            </div>

            <div>
              <Label className="my-1">
                Alternative/Backup Plan
                <span className="text-danger">*</span>
              </Label>
              <Controller
                name="altrBackupPlan"
                control={control}
                render={({ field }) => (
                  <Input
                    type="text"
                    placeholder="Enter Alternative/Backup Plan"
                    {...field}
                    className={`form-control external-filter-input ${errors.altrBackupPlan ? 'is-invalid' : ''}`}
                    disabled={formType === 'view'}
                    title="Mention your backup plan or point of contact during your absence."
                  />
                )}
              />
              {errors.altrBackupPlan && (
                <FormFeedback>{errors.altrBackupPlan.message}</FormFeedback>
              )}
            </div>

            <div>
              <Label className="my-1">
                <span>Upload Required Document</span>
              </Label>
              <Controller
                name="file"
                control={control}
                render={({ field }) => (
                  <FileUploadDropzone
                    error={errors.file}
                    onDrop={async acceptedFiles => {
                      try {
                        await validationSchema.fields.file.validate(
                          acceptedFiles,
                        );
                        clearErrors('files');
                        setValue('file', acceptedFiles, {
                          shouldValidate: true,
                        });
                      } catch (err) {
                        setError('file', {
                          type: 'manual',
                          message: err.message,
                        });
                      }
                    }}
                    tooltipTitle="Upload any mandatory documents for the leave you are requesting for (as proof)."
                    selectedFiles={field.value}
                    className={errors.file ? 'is-invalid' : ''}
                    handleDeleteFile={file => {
                      field.onChange(
                        field.value.filter(f => f.name !== file.name),
                      );
                      trigger();
                    }}
                    disabled={formType === 'view'}
                    showDeleteButton={formType !== 'view'}
                    showDownloadButton={formType === 'view'}
                    multiple
                  />
                )}
              />
              {errors.file && (
                <FormFeedback>{errors.file.message}</FormFeedback>
              )}
            </div>
            <div>
              <Label className="my-1">Comments</Label>
              <Controller
                name="comments"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder="Enter Comments"
                    type="textarea"
                    rows="2"
                    {...field}
                    className="form-control external-filter-input"
                    disabled={formType === 'view'}
                    title="Add any extra information or notes related to your leave."
                  />
                )}
              />
            </div>
            {formType !== 'view' && (
              <div className="d-flex flex-row align-items-center justify-content-center gap-2 p-3 pb-1">
                <Button
                  className="custom-light-btn"
                  style={{
                    width: '25%',
                  }}
                  onClick={() => reset()}
                  disabled={formType === 'view'}
                >
                  Clear
                </Button>
                <Button
                  className="custom-btn"
                  style={{
                    width: '25%',
                  }}
                  disabled={
                    !isValid || formType === 'view' || applyLeavePending
                  }
                  type="submit"
                >
                  <span>{applyLeavePending ? 'Submitting...' : 'Submit'}</span>
                </Button>
              </div>
            )}
          </Form>
          {errorData && errorData?.isTable && (
            <ErrorModalWrapper
              isModalOpen={errorModal}
              toggle={toggleErrorModal}
              errorMessage={errorData?.message}
              onButtonClick={toggleErrorModal}
              size={errorData?.isTable ? 'xl' : 'md'}
            >
              {errorData?.isTable && (
                <ErrorTable
                  errorData={errorData?.errorData}
                  dataConfig={modalConfig}
                />
              )}
            </ErrorModalWrapper>
          )}
        </div>

        <div className="col-5 leave-layout-border-left">
          <div className="tnc-wrapper">
            <button
              type="button"
              onClick={handleDepartmentLeaveStatus}
              className="custom-btn mx-auto my-3 department-leave-status-btn"
            >
              View Department Leave Status
            </button>
            <TimeOffHistory />
            <div className="leave-layout-separator" />
            <div
              className="px-3 pt-2 d-flex flex-column"
              style={{
                color: 'black',
              }}
            >
              <span className="tnc-title" style={{ letterSpacing: '1px' }}>
                Terms & Conditions
              </span>
              <span className="fw-bold my-1">Annual Leave</span>
              <p>
                23 Working days per year from 1st January to 31st December.
                Staff who have been employed for less than 12 months,
                Entitlement is proportionally reduced.
              </p>
              <span className="fw-bold mb-1 mt-2">Sick Leave</span>
              {companySet1.includes(selectedCoCd) && (
                <span className="mb-1">
                  All employees will be paid accordingly:
                </span>
              )}
              {companySet2.includes(selectedCoCd) && (
                <span className="mb-1">
                  An employee who proves their illness and need for sick leave,
                  based on a medical report from a medical authority approved by
                  the employer, is entitled to a maximum total of 120 days of
                  sick leave, whether consecutive or intermittent. The total
                  number of these days must be within the same illness year,
                  which begins on the date of the first sick leave and ends 12
                  months after the first sick leave the employee took.
                </span>
              )}
              {companySet1.includes(selectedCoCd) && (
                <ul>
                  <li>First 15 calendar days - full pay</li>
                  <li>Next 30 calendar days - half pay</li>
                  <li>Over 45 calendar days (up to 90 days) - without pay</li>
                </ul>
              )}
              {companySet2.includes(selectedCoCd) && (
                <ul>
                  <li>First 30 days - full pay</li>
                  <li>Next 60 days - 75% pay</li>
                  <li>Over 60 days and up to 120 days - No pay</li>
                </ul>
              )}
              <p className="mb-1">
                {companySet1.includes(selectedCoCd) && (
                  <span>
                    A medical certificate from a doctor (not necessarily
                    attested by the Health Authority) must be submitted to HR
                    when employee is on sick leave for more than 1 consecutive
                    days.
                  </span>
                )}
                {companySet2.includes(selectedCoCd) && (
                  <span>
                    A medical certificate must be attested by health authority
                    and linked to Sehaty.
                  </span>
                )}
              </p>
            </div>
            {companySet1.includes(selectedCoCd) && (
              <div className="d-flex flex-row align-items-center gap-1 px-3">
                <Icon path={mdiStar} size={1} color="#DC6803" />
                <span>Please</span>
                <a
                  href="https://denv.sharepoint.com/sites/eu-intra-dame-hr-en/SitePages/Leave-Management-Policy.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="clickable"
                >
                  click here
                </a>
                <span>to view the leave policy in detail.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApplyLeave;
